/**
 * Arithmetic editor for RECODE on FILL IN NUMBER / REAL targets. The user types
 * an expression (e.g. `(<<gross>> + <<bonus>>) * 0.7`); an "insert question"
 * dropdown injects `<<label>>` refs. The expression is validated live with
 * parseArith and errors are shown inline.
 */
import React, { useMemo } from "react"
import { Input, Select, Alert } from "antd"
import { LabelItem } from "utils/labelUtils"
import { parseArith } from "utils/recodeWizard/arithParser"

interface ArithmeticEditorProps {
  value: string
  /** Questions whose refs can be inserted (numeric sources). */
  refLabels: LabelItem[]
  onChange: (next: string) => void
}

const ArithmeticEditor: React.FC<ArithmeticEditorProps> = ({
  value,
  refLabels,
  onChange,
}) => {
  const errors = useMemo(
    () => (value.trim() ? parseArith(value).errors : []),
    [value],
  )

  const insertRef = (label: string) => {
    const ref = `<<${label}>>`
    const sep = value && !/\s$/.test(value) ? " " : ""
    onChange(`${value}${sep}${ref}`)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs text-gray-500">
        Compute the target from other questions and numbers using{" "}
        <code>+ - * /</code> and parentheses. Reference a question as{" "}
        <code>&lt;&lt;label&gt;&gt;</code>.
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Insert question</span>
        <Select
          showSearch
          size="small"
          style={{ minWidth: 260 }}
          placeholder="Add a <<question>> reference…"
          value={null}
          onChange={(label: string) => insertRef(label)}
          options={refLabels
            .map((l) => ({ value: l.name, label: l.name }))
            .sort((a, b) => a.label.localeCompare(b.label))}
        />
      </div>

      <Input.TextArea
        autoSize={{ minRows: 2, maxRows: 5 }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. (<<gross_salary>> + <<bonus>>) * 0.7"
        status={errors.length > 0 ? "error" : undefined}
        style={{ fontFamily: "monospace" }}
      />

      {errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message="This expression isn't valid yet"
          description={
            <ul className="list-disc pl-5 text-xs mb-0">
              {errors.slice(0, 4).map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          }
        />
      )}
    </div>
  )
}

export default ArithmeticEditor
